import { api } from './api';

export interface CommentRow {
  id: string;
  authorId: string;
  authorName: string;
  authorEmail: string;
  bodyMarkdown: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  reactions: Array<{ emoji: string; count: number; userIds: string[] }>;
  canEdit: boolean;
  canDelete: boolean;
  revisionsCount: number;
}

export interface TimeLogRow {
  id: string;
  userId: string;
  userName: string;
  minutes: number;
  loggedAt: string;
  description: string | null;
  createdAt: string;
  canEdit: boolean;
}

export async function listComments(
  orgId: string,
  issueId: string,
  opts: { limit?: number; offset?: number } = {},
) {
  const { data } = await api.get<{
    items: CommentRow[];
    total: number;
    limit: number;
    offset: number;
  }>(`/orgs/${orgId}/issues/${issueId}/comments`, { params: opts });
  return data;
}

export async function createComment(orgId: string, issueId: string, bodyMarkdown: string) {
  const { data } = await api.post<CommentRow>(`/orgs/${orgId}/issues/${issueId}/comments`, {
    bodyMarkdown,
  });
  return data;
}

export async function updateCommentBody(orgId: string, id: string, bodyMarkdown: string) {
  const { data } = await api.patch<CommentRow>(`/orgs/${orgId}/comments/${id}`, { bodyMarkdown });
  return data;
}

export async function deleteCommentApi(orgId: string, id: string) {
  await api.delete(`/orgs/${orgId}/comments/${id}`);
}

export async function toggleReactionApi(orgId: string, id: string, emoji: string) {
  await api.post(`/orgs/${orgId}/comments/${id}/reactions`, { emoji });
}

export async function listTimeLogs(orgId: string, issueId: string) {
  const { data } = await api.get<{ items: TimeLogRow[]; totalMinutes: number }>(
    `/orgs/${orgId}/issues/${issueId}/timelogs`,
  );
  return data;
}

export async function createTimeLog(
  orgId: string,
  issueId: string,
  body: { durationInput: string; loggedAt?: string; description?: string },
) {
  const { data } = await api.post<TimeLogRow>(`/orgs/${orgId}/issues/${issueId}/timelogs`, body);
  return data;
}

export async function updateTimeLogApi(
  orgId: string,
  id: string,
  body: { durationInput?: string; loggedAt?: string; description?: string | null },
) {
  const { data } = await api.patch<TimeLogRow>(`/orgs/${orgId}/timelogs/${id}`, body);
  return data;
}

export async function deleteTimeLogApi(orgId: string, id: string) {
  await api.delete(`/orgs/${orgId}/timelogs/${id}`);
}

export async function createIssueLink(
  orgId: string,
  issueId: string,
  body: {
    targetIssueId: string;
    linkType: 'BLOCKS' | 'IS_BLOCKED_BY' | 'RELATES_TO' | 'DUPLICATES' | 'DUPLICATED_BY';
  },
) {
  const { data } = await api.post<{ sourceLinkId: string; inverseLinkId: string }>(
    `/orgs/${orgId}/issues/${issueId}/links`,
    body,
  );
  return data;
}

export async function deleteIssueLinkApi(orgId: string, id: string) {
  await api.delete(`/orgs/${orgId}/links/${id}`);
}

export interface UploadResult {
  id: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  urls: { original: string; display?: string; thumbnail?: string };
}

export async function uploadFile(
  orgId: string,
  file: File,
  targets: { projectId?: string; issueId?: string; commentId?: string },
  onProgress?: (pct: number) => void,
): Promise<UploadResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('orgId', orgId);
  if (targets.projectId) form.append('projectId', targets.projectId);
  if (targets.issueId) form.append('issueId', targets.issueId);
  if (targets.commentId) form.append('commentId', targets.commentId);

  const { data } = await api.post<UploadResult>('/uploads', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) {
        onProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    },
  });
  return data;
}
